import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { Toast, type ToastMessage } from "@/components/ui/toast";

export interface ToastContextValue {
  showToast: (notification: ToastMessage) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [notification, setNotification] = useState<ToastMessage | null>(null);

  const showToast = useCallback((next: ToastMessage): void => {
    setNotification({ ...next });
  }, []);

  const dismissToast = useCallback((): void => {
    setNotification(null);
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {notification ? <Toast feedback={notification} onDismiss={dismissToast} /> : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return value;
}
